import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Stamp, Upload } from 'lucide-react';
import { problemDetail } from '@/shared/api/problem';
import {
  getApiV1ThemesBranding,
  getGetApiV1ThemeActiveQueryKey,
  useGetApiV1ThemeActive,
  usePostApiV1ThemesBranding,
  usePutApiV1ThemesId,
} from '@/shared/api/generated/theme/theme';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { QueryError } from '@/shared/ui/QueryError';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Panel } from '@/shared/ui/Panel';

/**
 * The four marks the certificate carries.
 *
 * ⚠️ Two were declared on every theme from the first migration (§7); the other two arrived with the
 * association's own certificate sample on 25 August 2026, by the client's instruction that **the
 * logos come from the dashboard**. That is the right home for a reason beyond preference: Vision
 * 2030 and the ministry licence are somebody else's marks under somebody else's brand rules, and one
 * that has to be corrected or withdrawn must not need a release of three clients to do it.
 */
const SLOTS = [
  { slot: 'logo', tokenKey: 'logoKey' },
  { slot: 'seal', tokenKey: 'certificateSealKey' },
  { slot: 'vision', tokenKey: 'visionKey' },
  { slot: 'licence', tokenKey: 'licenceKey' },
] as const;

type Slot = (typeof SLOTS)[number];

/**
 * The association's mark and its certificate seal (§7's `assets`).
 *
 * ⚠️ **The keys existed and nothing could write them.** Every theme has carried `logoKey` and
 * `certificateSealKey` since the first migration; the bucket held nothing under either, and changing
 * the seal meant a database session. The client's instruction of 25 August 2026 is that the seal
 * comes from the dashboard.
 */
export function Branding() {
  const { t } = useLocale();
  const queryClient = useQueryClient();

  const { data: theme, isPending, isError, error, refetch } = useGetApiV1ThemeActive();

  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const presign = usePostApiV1ThemesBranding();
  const saveTheme = usePutApiV1ThemesId();

  const tokens = parse(theme?.tokensJson);

  const upload = async (slot: Slot, file: File) => {
    setFailure(null);
    setBusy(slot.slot);

    try {
      const ticket = await presign.mutateAsync({
        data: { slot: slot.slot, contentType: file.type, sizeBytes: file.size },
      });

      // ⚠️ Straight to storage. §11 keeps bytes out of the API — this is the same presigned PUT the
      // volunteer documents use, and the reason the API never sees an image.
      const put = await fetch(String(ticket.uploadUrl), {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!put.ok) throw new Error(String(put.status));

      // ⚠️ The theme is written only AFTER the object exists. The other order leaves a theme
      // pointing at a key nothing was ever uploaded to, and the first thing to notice would be a
      // certificate with a hole where the seal goes.
      const next = { ...tokens, assets: { ...(tokens.assets ?? {}), [slot.tokenKey]: ticket.objectKey } };

      await saveTheme.mutateAsync({
        id: theme!.id!,
        data: { tokensJson: JSON.stringify(next) },
      });

      await queryClient.invalidateQueries({ queryKey: getGetApiV1ThemeActiveQueryKey() });
    } catch (problem) {
      setFailure(problemDetail(problem) ?? t.branding.uploadFailed);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t.branding.title} lede={t.branding.subtitle} />

      {isError && <QueryError error={error} onRetry={() => void refetch()} />}

      {failure && (
        <p role="alert" className="rounded-lg bg-error-50 p-3 text-sm text-error-600">
          {failure}
        </p>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {SLOTS.map((slot) => (
          <Asset
            key={slot.slot}
            slot={slot}
            objectKey={(tokens.assets ?? {})[slot.tokenKey] as string | undefined}
            disabled={isPending || busy !== null}
            busy={busy === slot.slot}
            onPick={(file) => void upload(slot, file)}
          />
        ))}
      </div>
    </div>
  );
}

function Asset({
  slot,
  objectKey,
  disabled,
  busy,
  onPick,
}: {
  slot: Slot;
  objectKey?: string;
  disabled: boolean;
  busy: boolean;
  onPick: (file: File) => void;
}) {
  const { t } = useLocale();
  const input = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  // ⚠️ Fetched per key, not stored. The link lives five minutes (§11), so holding one in state
  // across a session would show an administrator a broken image and no reason for it.
  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setMissing(false);

    if (!objectKey) return;

    getApiV1ThemesBranding({ objectKey })
      .then((link) => {
        if (!cancelled) setUrl(String(link));
      })
      .catch(() => {
        // ⚠️ A theme naming a key nothing was uploaded to. Said rather than shown as a blank frame,
        // because the fix — upload one — is not obvious from an empty box.
        if (!cancelled) setMissing(true);
      });

    return () => {
      cancelled = true;
    };
  }, [objectKey]);

  const label = t.branding.slots[slot.slot];
  const lede = t.branding.ledes[slot.slot];

  return (
    <Panel title={label} description={lede}>
      <div className="flex h-40 items-center justify-center rounded-lg bg-gray-50">
        {url ? (
          <img
            src={url}
            alt={label}
            className="max-h-36 max-w-full object-contain"
            onError={() => setMissing(true)}
          />
        ) : missing ? (
          <p className="px-4 text-center text-sm text-gray-500">{t.branding.keyMissing}</p>
        ) : objectKey ? (
          <p className="text-sm text-gray-400">{t.branding.loading}</p>
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-400">
            {slot.slot === 'seal' ? <Stamp className="h-8 w-8" /> : <ImageIcon className="h-8 w-8" />}
            <p className="text-sm">{t.branding.none}</p>
          </div>
        )}
      </div>

      <input
        ref={input}
        type="file"
        className="hidden"
        // ⚠️ The same three the server accepts. A picker that offers a PDF and a server that refuses
        // it makes the refusal look like a fault.
        accept="image/png,image/jpeg,image/svg+xml"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onPick(file);
          event.target.value = '';
        }}
      />

      <Button disabled={disabled} onClick={() => input.current?.click()}>
        <Upload className="h-4 w-4" />
        {busy ? t.branding.uploading : t.branding.replace}
      </Button>

      {/* ⚠️ Left to right and monospaced: it is a storage key somebody may have to read against a
          bucket listing, and an Arabic line mirrors the slashes in it. */}
      {objectKey && (
        <p dir="ltr" className="text-start font-mono text-xs text-gray-400">
          {objectKey}
        </p>
      )}
    </Panel>
  );
}

/**
 * ⚠️ Falls back to an empty document rather than throwing. The tokens are administration-editable
 * (§7), so a malformed one is a data fault — and a branding screen that white-screens on it is the
 * screen somebody would have used to fix it.
 */
function parse(json?: string | null): Record<string, unknown> & { assets?: Record<string, unknown> } {
  if (!json) return {};

  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
}
